import { defineStore } from 'pinia';
import {
  SET_CREATE_REGISTRY,
  SET_CREATE_REGISTRY_RESPONSE,
  SET_LOCATION_SERVICE_RESPONSE,
  SET_LOCATION_ERROR_STATE,
  SET_LOCATIONS_AVAILABLE,
  SET_CREATE_REGISTRY_ERROR_STATE,
  SET_CREATE_REGISTRY_BACKEND_ERROR_STATE,
  SET_REGISTRY_ID_STATUS,
  SET_CREATE_REGISTRY_ERROR_RESPONSE,
  SET_LOCATION_API_ERROR_RESPONSE,
  SET_CREATE_REGISTRY_LOADING,
  SET_CREATE_REGISTRY_API_ERROR_DESCRIPTION,
  SET_REGISTRY_STATUS_OCCASION,
} from '~/types/mutations';
import { CREATE_REGISTRY } from '~/types/actions';
import { httpUpdateAPIHeaders } from '~/utils/APIUtil';
import { generateLogin365FingerPrint } from '~/utils/PersistentLoginUtil';
import { SoftSignInValidation } from '~/utils/SoftSignInValidation.js';
import { useEnvPropsStore } from '~/stores/envProps';
import { useGoogleSecretsStore } from '~/stores/googleSecrets';
import $http from '@vue-util/http';

const CREATE_REGISTRY_ENDPOINT = '/regsvc/registry/api/v1/registry';
const LOCATION_SERVICE_ENDPOINT = '/commonlocations/v1/locations';

export const useCreateRegistryStore = defineStore('createRegistry', {
  state: () => ({
    createRegistry: {},
    createRegistryErrorState: false,
    createRegistryApiErrorResponse: null,
    createRegistryResponse: {},
    locationServiceResponse: {},
    locationErrorState: false,
    locationApiErrorResponse: null,
    locationsAvailable: false,
    registryIdExist: false,
    createRegistryLoading: false,
    createRegistryApiErrorDescription: '',
    createYourRegistryBackendError: false,
    registryStatusOccasion: '',
  }),
  actions: {
    [SET_CREATE_REGISTRY](createRegistry) {
      this.createRegistry = createRegistry;
    },
    [SET_CREATE_REGISTRY_ERROR_STATE](error) {
      this.createRegistryErrorState = error;
    },
    [SET_CREATE_REGISTRY_RESPONSE](response) {
      this.createRegistryResponse = response.data;
    },
    [SET_LOCATION_SERVICE_RESPONSE](response) {
      this.locationServiceResponse = response;
    },
    [SET_LOCATION_ERROR_STATE](payload) {
      this.locationErrorState = payload;
    },
    [SET_LOCATIONS_AVAILABLE](payload) {
      this.locationsAvailable = payload;
    },
    [SET_REGISTRY_ID_STATUS](payload) {
      this.registryIdExist = payload;
    },
    [SET_CREATE_REGISTRY_ERROR_RESPONSE](error) {
      this.createRegistryApiErrorResponse = error;
    },
    [SET_LOCATION_API_ERROR_RESPONSE](error) {
      this.locationApiErrorResponse = error;
    },
    [SET_CREATE_REGISTRY_LOADING](createRegistryLoading) {
      this.createRegistryLoading = createRegistryLoading;
    },
    [SET_CREATE_REGISTRY_API_ERROR_DESCRIPTION](createRegistryApiErrorDescription) {
      this.createRegistryApiErrorDescription = createRegistryApiErrorDescription;
    },
    [SET_CREATE_REGISTRY_BACKEND_ERROR_STATE](error) {
      this.createYourRegistryBackendError = error;
    },
    [SET_REGISTRY_STATUS_OCCASION](payload) {
      this.registryStatusOccasion = payload;
    },
    async [CREATE_REGISTRY](payload) {
      this[SET_CREATE_REGISTRY_LOADING](true);
      try {
        const login365FingerPrint = await generateLogin365FingerPrint();
        const response = await $http.post(`${CREATE_REGISTRY_ENDPOINT}`, payload, {
          headers: httpUpdateAPIHeaders(payload, login365FingerPrint, true),
        });
        const isError = response?.data?.errors || response?.data?.error;
        if (isError) {
          this[SET_CREATE_REGISTRY_RESPONSE]({});
          this[SET_CREATE_REGISTRY_ERROR_STATE](true);
          this[SET_CREATE_REGISTRY_API_ERROR_DESCRIPTION](isError?.[0]?.description);
          this[SET_CREATE_REGISTRY_ERROR_RESPONSE](isError?.[0]);
        } else if (!response.data?.registryInfo?.registryId) {
          this[SET_CREATE_REGISTRY_BACKEND_ERROR_STATE](true);
          this[SET_CREATE_REGISTRY_ERROR_STATE](true);
          this[SET_CREATE_REGISTRY_RESPONSE]({});
        } else {
          this[SET_CREATE_REGISTRY_RESPONSE](response);
          this[SET_CREATE_REGISTRY_BACKEND_ERROR_STATE](false);
          this[SET_CREATE_REGISTRY_ERROR_STATE](false);
        }
      } catch (error) {
        SoftSignInValidation(error);
        this[SET_CREATE_REGISTRY_RESPONSE]({});
        this[SET_CREATE_REGISTRY_API_ERROR_DESCRIPTION]('');
        this[SET_CREATE_REGISTRY_ERROR_STATE](true);
        this[SET_CREATE_REGISTRY_ERROR_RESPONSE](error?.response?.data?.errors?.[0] || error?.response?.data?.error?.[0] || null);
      } finally {
        this[SET_CREATE_REGISTRY_LOADING](false);
      }
    },
    async getLocationServiceInfo(params) {
      const envPropsStore = useEnvPropsStore();
      const googleSecretsStore = useGoogleSecretsStore();

      const httpSource = $http.CancelToken?.source();
      const timer = setTimeout(() => {
        this[SET_LOCATION_SERVICE_RESPONSE]({});
        this[SET_LOCATION_ERROR_STATE](true);
        httpSource.cancel();
      }, envPropsStore.envProps?.apiTimeout || 0);
      try {
        const response = await $http.get(`${LOCATION_SERVICE_ENDPOINT}/divn/${params.orgId}/zipcode/${params.zipCode}?radius=100`, {
          headers: {
            'X-Client-Id': params.orgId === 12 || params.orgId === '12' ? 'MCOM' : 'BCOM',
            'X-Sub-Client-Id': params.orgId === 12 || params.orgId === '12' ? 'MCOM' : 'BCOM',
            'x-macys-apikey': googleSecretsStore.xMacysApiKey,
            Accept: 'application/json',
          },
          cancelToken: httpSource.token,
        });
        clearTimeout(timer);
        const locations = response?.data?.items;
        const defaultLocation = {
          name: 'Herald Square',
          storeNumber: 3,
          addresses: [
            {
              addrLine1: '151 West 34th Street',
              city: 'New York',
              state: 'NY',
              zipCode: '10001',
            },
          ],
        };
        if (params.orgId === 12) {
          const validLocation = locations?.find((location) =>
            location.attributes.find((attribute) => attribute.name.toUpperCase() === 'SL_BRIDAL' && attribute.value[0] === 'TRUE'),
          );
          this[SET_LOCATION_SERVICE_RESPONSE]({ items: validLocation ? [validLocation] : [defaultLocation] });
        } else {
          this[SET_LOCATION_SERVICE_RESPONSE]({ items: locations?.[0] ? [locations?.[0]] : [defaultLocation] });
        }
        this[SET_LOCATIONS_AVAILABLE](true);
      } catch (error) {
        clearTimeout(timer);
        this[SET_LOCATION_SERVICE_RESPONSE]({});
        this[SET_LOCATIONS_AVAILABLE](false);
        this[SET_LOCATION_API_ERROR_RESPONSE](error);
      }
    },
  },
});
